import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import { Col, Row } from 'antd'

import OrderedPageFilter from 'core/application/commons/orderedPageFilter'
import { ExpenseAttachment } from 'core/application/docReviewDashboard/expenses/expenseAttachment'
import searchExpeneses, { PagedExpenses } from 'core/application/docReviewDashboard/searchExpenses/searchExpenses'

import Card from 'common/components/dataDisplay/Card'
import notification from 'common/components/feedback/Notification'

import { queryRequest } from 'common/RequestUtils'

import ViewExpenseAttachment from '../common/ViewExpenseAttachment'
import ExpensesTable from './ExpensesTable'
import SearchFilter from './SearchFilters'

import './SearchExpense.less'

export const SEARCH_EXPENSES_PATH = 'search-Expenses'

const pagedColumnDefaultValues = {
  items: [],
  pageCount: 1,
  pageNumber: 1,
  pageSize: 10,
  totalItemCount: 0,
}

const defaultFilter = {
  pageNumber: 1,
  pageSize: 10,
  orderColumn: 'createdAt',
  orderDescending: false,
  searchValue: null,
  categoryId: null,
}

type ExpenseSearchValue = {
  dateOfService: string | null
  lastModifiedAt: string | null
  inputSearch: string | null
}

const CsiSearch: React.FC = () => {
  const { t: translate } = useTranslation()
  const history = useHistory()
  const [searchFilter, setSearchFilter] = useState<OrderedPageFilter>(defaultFilter)
  const [loadingExpenses, SetLoadingExpenses] = useState<boolean>(false)
  const [pagedExpenses, setPagedExpenses] = useState<PagedExpenses>(pagedColumnDefaultValues)
  const [expenseSearchValue, setExpenseSearchValue] = useState<ExpenseSearchValue | null>(null)
  const [viewAttachments, setViewAttachments] = useState<boolean>(false)
  const [activeModalAttachments, setActiveModalAttachments] = useState<ExpenseAttachment[]>([])

  useEffect(() => {
    const filter = {
      ...searchFilter,
      searchValue: expenseSearchValue?.inputSearch ?? null,
      dateOfService: expenseSearchValue?.dateOfService ?? null,
      lastModifiedAt: expenseSearchValue?.lastModifiedAt ?? null,
    }
    if (expenseSearchValue) updateExpensesTable(filter)
  }, [searchFilter])

  const updateExpensesTable = async (searchFilter: OrderedPageFilter) => {
    SetLoadingExpenses(true)
    const result = await queryRequest(() => searchExpeneses(searchFilter))
    setPagedExpenses(result)
    SetLoadingExpenses(false)
  }

  const onSearch = (searchValue: ExpenseSearchValue) => {
    if (searchValue.inputSearch && searchValue.inputSearch?.length < 1) {
      notification.open({
        type: 'error',
        message: translate('general.search-length'),
      })
    } else {
      setExpenseSearchValue(searchValue)
      setSearchFilter({
        ...searchFilter,
        searchValue: searchValue.inputSearch,
        dateOfService: searchValue.dateOfService,
        lastModifiedAt: searchValue.lastModifiedAt,
      })
    }
  }

  const showHideDocumentsModal = () => {
    setViewAttachments(!viewAttachments)
  }

  return (
    <>
      <Card title={translate('doc-review-dashboard.search-expenses.search-title')}>
        <Row>
          <Col span={18}>
            <SearchFilter translate={translate} onSearch={onSearch} loading={loadingExpenses} />
          </Col>
        </Row>

        {expenseSearchValue && (
          <Row className="mt-16">
            <Col span={24}>
              <h5>
                {pagedExpenses.totalItemCount} {translate('general.search-result-text')} "
                {expenseSearchValue.inputSearch ?? ''} {expenseSearchValue.dateOfService ?? ''}"
              </h5>
            </Col>

            <Col span={24}>
              {pagedExpenses.totalItemCount > 0 && (
                <ExpensesTable
                  setSearchFilter={setSearchFilter}
                  expenses={pagedExpenses}
                  loadingExpenses={loadingExpenses}
                  history={history}
                  translate={translate}
                  setViewAttachments={showHideDocumentsModal}
                  setActiveModalAttachments={setActiveModalAttachments}
                />
              )}

              <ViewExpenseAttachment
                attachments={activeModalAttachments}
                visible={viewAttachments}
                onClose={() => setViewAttachments(false)}
              />
            </Col>

            <Col>
              {pagedExpenses.totalItemCount === 0 && !loadingExpenses && (
                <div className="gray_children">
                  <h4> {translate('doc-review-dashboard.search-expenses.search-tips.title')}</h4>
                  <span>{translate('doc-review-dashboard.search-expenses.search-tips.insensitive')}</span>
                  <br />
                  <span>{translate('doc-review-dashboard.search-expenses.search-tips.quoted')}</span>
                  <br />
                  <span>{translate('doc-review-dashboard.search-expenses.search-tips.and-terms')}</span>
                  <br />
                  <span>{translate('doc-review-dashboard.search-expenses.search-tips.not-terms')}</span>
                  <br />
                  <span>{translate('doc-review-dashboard.search-expenses.search-tips.group-terms')}</span>
                  <br />
                  <span>{translate('doc-review-dashboard.search-expenses.search-tips.prefix-terms')}</span>
                </div>
              )}
            </Col>
          </Row>
        )}
      </Card>
    </>
  )
}

export default CsiSearch
